import { useCanViewEmails } from '../../../helpers/useCanViewEmails';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { useHotkeys } from 'react-hotkeys-hook';
import { useHistory } from 'react-router-dom';
import { Tooltip } from '@mui/material';

import { getSubGroupsByGroupIdThunk } from '../../../store/subGroups/subGroupsSlice';
import { ContainedButton, OutlinedButton } from '../../modules/Buttons';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { patchSubGroupAnalysts } from '../../../api/subGroupsApi';
import { SUBGROUP_LIST_ITEM, USER } from '../../../enums/TYPES';
import LinearProgress from '../../modules/LinearProgress';
import styles from './UpdateAnalystModal.module.scss';
import SearchInput from '../../modules/SearchInput';
import RolePlate from '../../modules/RolePlate';
import { RolesLowerCase } from '../../../enums';
import Avatar from '../../modules/Avatar';
import Modal from '../../modules/Modal';

type TUpdateAnalystModal = {
  open: boolean;
  switchModal: () => void;
  subGroup: SUBGROUP_LIST_ITEM;
};

const UpdateAnalystModal: React.FC<TUpdateAnalystModal> = ({ open, switchModal, subGroup }) => {
  const canViewEmails = useCanViewEmails();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const [analystsToAdd, setAnalystsToAdd] = useState<string[]>([]);
  const [analystsToRemove, setAnalystsToRemove] = useState<string[]>([]);
  const [errMsg, setErrMsg] = useState('');
  const { currentGroup, sort, sortOption } = useAppSelector((state) => state.subGroupsStore);
  const [currGroup, setCurrGroup] = useState<USER[]>([]);
  const [allGroupUsers, setAllGroupUsers] = useState<USER[]>();
  const [loading, setLoading] = useState(false);
  const [newTitle, setNewTitle] = useState<string>('');
  const submitButtonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (currentGroup) {
      setCurrGroup([...subGroup.analysts, ...subGroup.viewers]);
    }
  }, [currentGroup]);

  useEffect(() => {
    if (currentGroup) {
      setCurrGroup([...subGroup.analysts, ...subGroup.viewers]);
      setAllGroupUsers([...currentGroup.analysts, ...currentGroup.viewers]);
    }
  }, [currentGroup, open]);

  const assignedUsersIds = useMemo(() => {
    return [...(subGroup?.analysts.map((analyst) => analyst.id) || []), ...(subGroup?.viewers.map((analyst) => analyst.id) || [])];
  }, [subGroup]);

  const userChips = useMemo(() => {
    const listOfChips =
      allGroupUsers &&
      allGroupUsers.filter((u) => !analystsToRemove.includes(u.id) && (analystsToAdd.includes(u.id) || assignedUsersIds.includes(u.id)));

    return listOfChips?.map((u) => (
      <div key={`chip_${u.id}`} className={styles.chip}>
        <span>{u?.username ? u?.username : (canViewEmails ? u?.email : 'User')}</span>{' '}
        <CloseIcon
          onClick={() => onChange({ target: { id: u.id } })}
          className={styles.removeChip}
          sx={{ color: 'black', fontSize: '14px', marginLeft: '8px' }}
        />
      </div>
    ));
  }, [allGroupUsers, currentGroup, analystsToAdd, assignedUsersIds, analystsToRemove, open]);

  const onChange = (e: any) => {
    setErrMsg('');
    if (e.target.checked) {
      const removeList = analystsToRemove.filter((id) => id !== e.target.id);
      setAnalystsToAdd([...analystsToAdd, e.target.id]);
      setAnalystsToRemove(removeList);
    } else {
      const addList = analystsToAdd.filter((id) => id !== e.target.id);
      setAnalystsToRemove([...analystsToRemove, e.target.id]);
      setAnalystsToAdd(addList);
    }
  };

  const onSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    setErrMsg('');

    if (e.target.checked) {
      setAnalystsToAdd(allGroupUsers!.map((user) => user.id));
    } else {
      setAnalystsToAdd([]);
      setAnalystsToRemove(assignedUsersIds);
    }
  };

  const onSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.value) {
      setAllGroupUsers([
        ...currentGroup!.analysts.filter((analyst) => !currGroup.find((user) => user.id === analyst.id)),
        ...currentGroup!.viewers.filter((viewer) => !currGroup.find((user) => user.id === viewer.id)),
      ]);
      setCurrGroup([...subGroup.analysts, ...subGroup.viewers]);
      return;
    }
    setAllGroupUsers([
      ...currentGroup!.analysts.filter(
        (analyst) =>
          !currGroup.find((user) => user.id === analyst.id) &&
          (analyst?.username?.includes(e.target.value) || analyst.email.includes(e.target.value)),
      ),
      ...currentGroup!.viewers.filter(
        (viewer) =>
          !currGroup.find((user) => user.id === viewer.id) &&
          (viewer?.username?.includes(e.target.value) || viewer.email.includes(e.target.value)),
      ),
    ]);
    setCurrGroup([
      ...subGroup.analysts.filter((analyst) => analyst?.username?.includes(e.target.value) || analyst.email.includes(e.target.value)),
      ...subGroup.viewers.filter((viewer) => viewer?.username?.includes(e.target.value) || viewer.email.includes(e.target.value)),
    ]);
  };

  const onClose = () => {
    switchModal();
    setAnalystsToAdd([]);
    setAnalystsToRemove([]);
    setLoading(false);
    setErrMsg('');
    setNewTitle('');
    setAllGroupUsers([
      ...(currentGroup!.analysts.filter((analyst) => !currGroup.find((user) => user.id === analyst.id)) || []),
      ...(currentGroup!.viewers.filter((viewer) => !currGroup.find((user) => user.id === viewer.id)) || []),
    ]);
    setCurrGroup([...subGroup.analysts, ...subGroup.viewers]);
  };

  const onSubmit = async () => {
    setLoading(true);
    try {
      await patchSubGroupAnalysts(subGroup.id, {
        usersToAdd: analystsToAdd,
        usersToRemove: analystsToRemove,
        groupId: currentGroup!.id,
      });
      dispatch(getSubGroupsByGroupIdThunk({ groupId: history.location.state as string, sort, sortOption }));
      onClose();
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const displayRole = (roleTitle: string) => {
    switch (roleTitle) {
      case RolesLowerCase.ORGANIZATION_ADMIN:
        return 'OA';
      case RolesLowerCase.ANALYST:
        return 'Analyst';
      case RolesLowerCase.VIEWER:
        return 'Viewer';
      default:
        return '';
    }
  };

  useHotkeys('Enter', () => {
    if (!open || loading) return;

    submitButtonRef?.current?.click();
  });

  return (
    <Modal open={open} onClose={onClose} loading={loading}>
      <div className={styles.modalBody}>
        <div className={styles.modalHeader}>
          <div className={styles.title}>Edit Participants</div>
        </div>
        <LinearProgress className={styles.progressBar} variant="determinate" value={100} />
        <div className={styles.modalContainer}>
          <SearchInput className={styles.searchInput} placeHolder="Search by email/name" onChange={onSearch} />
          <div className={styles.userChips}>{userChips}</div>
          <div className={styles.analystList}>
            <label htmlFor="selectAll">
              <div className={styles.headingLine}>
                <div className={styles.headingInfo}>
                  <div className={styles.heading}>
                    <div className={styles.headingName}>
                      Select All <div>{currentGroup?.title}</div> users
                    </div>
                  </div>
                </div>
                <input id="selectAll" onChange={(e) => onSelectAll(e)} type="checkbox" />
              </div>
            </label>
            {allGroupUsers
              ?.filter((value, index, self) => self.findIndex((v) => v.id === value.id) === index)
              .map((analyst, i) => (
                <label htmlFor={analyst.id} key={`${analyst.id}_${i}`}>
                  <div className={styles.userLine}>
                    <div className={styles.userInfo}>
                      <Avatar name={analyst.email} avatar={analyst.avatar_url} />
                      <div className={styles.info}>
                        <Tooltip title={analyst?.username}>
                          <div className={styles.name}>{analyst.username}</div>
                        </Tooltip>
                        {canViewEmails && (<Tooltip title={analyst.email}>
                          <div className={styles.email}>{analyst.email}</div>
                        </Tooltip>)}
                      </div>
                    </div>
                    <div className={styles.horizontalContainer}>
                      <RolePlate userRole={displayRole(analyst.role.title)} />
                      <input
                        onChange={onChange}
                        id={analyst.id}
                        type="checkbox"
                        // defaultChecked={assignedUsersIds?.includes(analyst.id)}
                        checked={
                          (assignedUsersIds?.includes(analyst.id) && !analystsToRemove.includes(analyst.id)) ||
                          analystsToAdd.includes(analyst.id)
                        }
                      />
                    </div>
                  </div>
                </label>
              ))}
          </div>
          <span className={styles.errMsg}>{errMsg}</span>
          <div className={styles.actionBtns}>
            <ContainedButton onClick={onSubmit} disabled={loading || (analystsToAdd.length === 0 && analystsToRemove.length === 0)}>
              <div ref={submitButtonRef}>Select</div>
            </ContainedButton>
            <OutlinedButton onClick={onClose}>Cancel</OutlinedButton>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default UpdateAnalystModal;
